import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  conciliacionesAPI,
  cuentasBancariasAPI,
  tiposProveedoresAPI,
  clientesAPI,
  proveedoresAPI,
  empleadosAPI,
  cajasAPI,
  recibosCobroAPI,
  withSaldoNegativoConfirm,
} from '../services/api';
import { useZonaHoraria } from '../context/ZonaHorariaContext';
import ListadoScrollLayout from '../components/ListadoScrollLayout';
import FiltroPredefinidosMultiselect from '../components/FiltroPredefinidosMultiselect';
import '../Clientes.css';
import './ConciliacionBancaria.css';

const formatMoney = (n) => {
  const v = parseFloat(n) || 0;
  const abs = Math.abs(v).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (v < 0) return `-$${abs}`;
  return `$${abs}`;
};

const estadoLabel = {
  conciliado: 'Conciliado',
  solo_banco: 'Solo banco',
  solo_sistema: 'Solo sistema',
  diferencia_monto: 'Diferencia',
  ignorado: 'Ignorado',
  cancelado_sistema: 'Cancelado sist.',
};

const estadoClass = {
  conciliado: 'badge-success',
  solo_banco: 'badge-warning',
  solo_sistema: 'badge-danger',
  diferencia_monto: 'badge-warning',
  ignorado: '',
  cancelado_sistema: 'badge-success',
};

const ESTADOS_REVERTIBLES = ['conciliado', 'ignorado', 'cancelado_sistema', 'diferencia_monto'];

const DESTINOS_CREDITO = [
  { id: 'cliente', label: 'Cliente (recibo)' },
  { id: 'deposito_cheque', label: 'Depósito de cheque(s)' },
  { id: 'deposito_efectivo', label: 'Depósito en efectivo (caja)' },
  { id: 'credito_bancario', label: 'Crédito bancario' },
];

const DESTINOS_DEBITO = [
  { id: 'gasto', label: 'Gasto' },
  { id: 'proveedor', label: 'Proveedor (OP)' },
  { id: 'empleado', label: 'Empleado (pago)' },
];

const COLUMNAS_MATCH = [
  { id: 'estado', label: 'Estado' },
  { id: 'banco_fecha', label: 'F. banco' },
  { id: 'banco_concepto', label: 'Concepto banco' },
  { id: 'importe', label: 'Importe' },
  { id: 'sistema_fecha', label: 'F. sistema' },
  { id: 'sistema_concepto', label: 'Concepto sistema' },
  { id: 'sistema_monto', label: 'Monto sist.' },
];

const FILTROS_MATCH_INICIAL = {
  estados: [],
  banco_fecha_desde: '',
  banco_fecha_hasta: '',
  banco_concepto: '',
  importe_min: '',
  importe_max: '',
  sistema_fecha_desde: '',
  sistema_fecha_hasta: '',
  sistema_concepto: '',
  monto_sist_min: '',
  monto_sist_max: '',
};

function fechaSortKey(f) {
  if (!f) return 0;
  const t = new Date(`${String(f).slice(0, 10)}T12:00:00`).getTime();
  return Number.isFinite(t) ? t : 0;
}

function getSortValueMatch(m, colId) {
  switch (colId) {
    case 'estado':
      return (m.estado || '').toLowerCase();
    case 'banco_fecha':
      return fechaSortKey(m.banco_fecha);
    case 'banco_concepto':
      return (m.banco_concepto || '').toLowerCase();
    case 'importe':
      return importeBanco(m).valor;
    case 'sistema_fecha':
      return fechaSortKey(m.sistema_fecha);
    case 'sistema_concepto':
      return (m.sistema_concepto || '').toLowerCase();
    case 'sistema_monto':
      return importeSistema(m).valor;
    default:
      return '';
  }
}

function pasaFiltroFecha(valor, desde, hasta) {
  const v = valor ? String(valor).slice(0, 10) : '';
  if (desde && (!v || v < desde)) return false;
  if (hasta && (!v || v > hasta)) return false;
  return true;
}

function pasaFiltroNumero(valor, minStr, maxStr) {
  const n = parseFloat(valor) || 0;
  if (minStr !== '' && minStr != null) {
    const min = parseFloat(minStr);
    if (!Number.isNaN(min) && n < min) return false;
  }
  if (maxStr !== '' && maxStr != null) {
    const max = parseFloat(maxStr);
    if (!Number.isNaN(max) && n > max) return false;
  }
  return true;
}

function pasaFiltroTexto(valor, q) {
  const needle = String(q || '').trim().toLowerCase();
  if (!needle) return true;
  return String(valor || '').toLowerCase().includes(needle);
}

function importeBanco(m) {
  const deb = parseFloat(m.banco_debito) || 0;
  const cred = parseFloat(m.banco_credito) || 0;
  if (cred > 0) return { valor: cred, signo: 1 };
  if (deb > 0) return { valor: -deb, signo: -1 };
  return { valor: 0, signo: 0 };
}

/** Monto de sistema con signo: efecto sobre la cuenta (débito/crédito), fallback a tipo. */
function importeSistema(m) {
  const deb = parseFloat(m.sistema_debito) || 0;
  const cred = parseFloat(m.sistema_credito) || 0;
  if (cred > 0) return { valor: cred, signo: 1, abs: cred, tipo: m.sistema_tipo || null };
  if (deb > 0) return { valor: -deb, signo: -1, abs: deb, tipo: m.sistema_tipo || null };
  if (m.sistema_importe != null && m.sistema_importe !== '') {
    const v = parseFloat(m.sistema_importe) || 0;
    if (v !== 0) {
      return { valor: v, signo: v < 0 ? -1 : 1, abs: Math.abs(v), tipo: m.sistema_tipo || null };
    }
  }
  const abs = Math.abs(parseFloat(m.sistema_monto) || 0);
  if (!abs) return { valor: 0, signo: 0, abs: 0, tipo: m.sistema_tipo || null };
  const tipo = String(m.sistema_tipo || '').toLowerCase();
  if (tipo === 'egreso') return { valor: -abs, signo: -1, abs, tipo };
  if (tipo === 'ingreso') return { valor: abs, signo: 1, abs, tipo };
  return { valor: abs, signo: 1, abs, tipo: tipo || null };
}

function ConciliacionDetalle({ id }) {
  const { formatFecha } = useZonaHoraria();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [importing, setImporting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [destino, setDestino] = useState('gasto');
  const [tipoProveedorId, setTipoProveedorId] = useState('');
  const [clienteId, setClienteId] = useState('');
  const [proveedorId, setProveedorId] = useState('');
  const [empleadoId, setEmpleadoId] = useState('');
  const [cajaOrigenId, setCajaOrigenId] = useState('');
  const [chequeIds, setChequeIds] = useState([]);
  const [tiposGasto, setTiposGasto] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [cajas, setCajas] = useState([]);
  const [chequesCartera, setChequesCartera] = useState([]);
  const [chequesDepositados, setChequesDepositados] = useState([]);
  const [modalChequesTipo, setModalChequesTipo] = useState(null); // 'acreditar' | 'rechazar'
  const [chequeAccionIds, setChequeAccionIds] = useState([]);
  const [selectedMf, setSelectedMf] = useState([]);
  const [selectedMatchIds, setSelectedMatchIds] = useState([]);
  const [filtrosMatch, setFiltrosMatch] = useState(() => ({ ...FILTROS_MATCH_INICIAL }));
  const [sortByColumn, setSortByColumn] = useState(null);
  const [sortAsc, setSortAsc] = useState(true);
  const [cuentasMover, setCuentasMover] = useState([]);
  const [modalMoverLinea, setModalMoverLinea] = useState(null); // match row
  const [cuentaMoverId, setCuentaMoverId] = useState('');
  const [filtroChequeAccion, setFiltroChequeAccion] = useState({
    texto: '',
    monto_min: '',
    monto_max: '',
    fecha_desde: '',
    fecha_hasta: '',
  });
  const navigate = useNavigate();

  const cargar = useCallback(async () => {
    try {
      setLoading(true);
      const res = await conciliacionesAPI.getById(id);
      if (res.success) setData(res.data);
      else toast.error(res.message || 'Error al cargar');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  useEffect(() => {
    (async () => {
      try {
        const [t, c, p, e, cj] = await Promise.all([
          tiposProveedoresAPI.getAll(),
          clientesAPI.getAll({ activo: true }),
          proveedoresAPI.getAll({ activo: true }),
          empleadosAPI.getAll({ activo: true }),
          cajasAPI.getAll({ activo: true }),
        ]);
        if (t.success) setTiposGasto((t.data || []).filter((x) => x.activo !== 0 && x.activo !== false));
        if (c.success) setClientes(c.data || []);
        if (p.success) setProveedores(p.data || []);
        if (e.success) setEmpleados(e.data || []);
        if (cj.success) setCajas((cj.data || []).filter((x) => x.activo !== 0 && x.activo !== false));
      } catch (_) {
        /* catálogos opcionales al fallar */
      }
    })();
  }, []);

  const matchesSeleccionados = useMemo(
    () => (data?.matches || []).filter((m) => m.estado === 'solo_banco' && selected.includes(m.linea_banco_id)),
    [data, selected]
  );

  const resumenSeleccion = useMemo(() => {
    let debitos = 0;
    let creditos = 0;
    for (const m of matchesSeleccionados) {
      debitos += parseFloat(m.banco_debito) || 0;
      creditos += parseFloat(m.banco_credito) || 0;
    }
    let signo = null;
    if (creditos > 0 && debitos > 0) signo = 'mixto';
    else if (creditos > 0) signo = 'credito';
    else if (debitos > 0) signo = 'debito';
    const netoBanco = creditos - debitos;
    return { n: matchesSeleccionados.length, debitos, creditos, signo, netoBanco };
  }, [matchesSeleccionados]);

  const matchesMfSeleccionados = useMemo(
    () => (data?.matches || []).filter(
      (m) => m.estado === 'solo_sistema' && selectedMf.includes(m.movimiento_fondo_id)
    ),
    [data, selectedMf]
  );

  const netoSistemaSel = useMemo(() => {
    let neto = 0;
    for (const m of matchesMfSeleccionados) {
      neto += importeSistema(m).valor;
    }
    return neto;
  }, [matchesMfSeleccionados]);

  const puedeVincularNM = useMemo(() => {
    if (!matchesSeleccionados.length || !matchesMfSeleccionados.length) return false;
    return Math.abs(resumenSeleccion.netoBanco - netoSistemaSel) <= 0.02;
  }, [matchesSeleccionados, matchesMfSeleccionados, resumenSeleccion.netoBanco, netoSistemaSel]);

  const puedeCancelarSistema = useMemo(() => {
    if (matchesMfSeleccionados.length < 2) return false;
    return Math.abs(netoSistemaSel) <= 0.02;
  }, [matchesMfSeleccionados, netoSistemaSel]);

  const puedeIgnorar = useMemo(() => {
    const soloUnBanco =
      selected.length === 1 && selectedMf.length === 0 && matchesSeleccionados.length === 1;
    const soloUnSist =
      selectedMf.length === 1 && selected.length === 0 && matchesMfSeleccionados.length === 1;
    return soloUnBanco || soloUnSist;
  }, [selected, selectedMf, matchesSeleccionados, matchesMfSeleccionados]);

  const puedeRevertir = useMemo(() => {
    if (!selectedMatchIds.length || selected.length || selectedMf.length) return false;
    const byId = new Map((data?.matches || []).map((m) => [m.id, m]));
    return selectedMatchIds.every((mid) => ESTADOS_REVERTIBLES.includes(byId.get(mid)?.estado));
  }, [selectedMatchIds, selected.length, selectedMf.length, data]);

  const netoSeleccionStats = useMemo(() => {
    const hayBanco = selected.length > 0;
    const haySist = selectedMf.length > 0;
    if (hayBanco && haySist) return resumenSeleccion.netoBanco - netoSistemaSel;
    if (hayBanco) return resumenSeleccion.netoBanco;
    if (haySist) return netoSistemaSel;
    return 0;
  }, [selected.length, selectedMf.length, resumenSeleccion.netoBanco, netoSistemaSel]);

  const destinosDisponibles = useMemo(() => {
    if (resumenSeleccion.signo === 'credito') return DESTINOS_CREDITO;
    if (resumenSeleccion.signo === 'debito') return DESTINOS_DEBITO;
    return [];
  }, [resumenSeleccion.signo]);

  const matchesFiltradosOrdenados = useMemo(() => {
    const f = filtrosMatch;
    let rows = (data?.matches || []).filter((m) => {
      if (Array.isArray(f.estados) && f.estados.length > 0 && !f.estados.includes(m.estado)) return false;
      if (!pasaFiltroFecha(m.banco_fecha, f.banco_fecha_desde, f.banco_fecha_hasta)) return false;
      if (!pasaFiltroTexto(m.banco_concepto, f.banco_concepto)) return false;
      if (!pasaFiltroNumero(importeBanco(m).valor, f.importe_min, f.importe_max)) return false;
      if (!pasaFiltroFecha(m.sistema_fecha, f.sistema_fecha_desde, f.sistema_fecha_hasta)) return false;
      if (!pasaFiltroTexto(m.sistema_concepto, f.sistema_concepto)) return false;
      if (!pasaFiltroNumero(importeSistema(m).valor, f.monto_sist_min, f.monto_sist_max)) return false;
      return true;
    });
    if (sortByColumn) {
      rows = [...rows].sort((a, b) => {
        const v1 = getSortValueMatch(a, sortByColumn);
        const v2 = getSortValueMatch(b, sortByColumn);
        const num = typeof v1 === 'number' && typeof v2 === 'number';
        let cmp = 0;
        if (num) cmp = v1 - v2;
        else cmp = String(v1).localeCompare(String(v2), 'es');
        return sortAsc ? cmp : -cmp;
      });
    }
    return rows;
  }, [data, filtrosMatch, sortByColumn, sortAsc]);

  const setFiltroMatch = (key, value) => {
    setFiltrosMatch((prev) => ({ ...prev, [key]: value }));
  };

  const limpiarFiltrosMatch = () => setFiltrosMatch({ ...FILTROS_MATCH_INICIAL });

  const hayFiltrosMatch = useMemo(
    () => Object.entries(filtrosMatch).some(([, v]) => {
      if (Array.isArray(v)) return v.length > 0;
      return String(v ?? '').trim() !== '';
    }),
    [filtrosMatch]
  );

  const handleSortColumn = (columnId) => {
    if (sortByColumn === columnId) setSortAsc((prev) => !prev);
    else {
      setSortByColumn(columnId);
      setSortAsc(true);
    }
  };

  const sumaChequesSeleccionados = useMemo(
    () =>
      chequesCartera
        .filter((ch) => chequeIds.includes(ch.id))
        .reduce((s, ch) => s + (parseFloat(ch.importe) || 0), 0),
    [chequesCartera, chequeIds]
  );

  const sumaChequesAccion = useMemo(
    () =>
      chequesDepositados
        .filter((ch) => chequeAccionIds.includes(ch.id))
        .reduce((s, ch) => s + (parseFloat(ch.importe) || 0), 0),
    [chequesDepositados, chequeAccionIds]
  );

  const toggleSelect = (lineaId) => {
    if (lineaId == null || lineaId === '') return;
    setSelectedMatchIds([]);
    setSelected((prev) =>
      prev.includes(lineaId) ? prev.filter((x) => x !== lineaId) : [...prev, lineaId]
    );
  };

  const toggleSelectMf = (mfId) => {
    if (mfId == null || mfId === '') return;
    setSelectedMatchIds([]);
    setSelectedMf((prev) =>
      prev.includes(mfId) ? prev.filter((x) => x !== mfId) : [...prev, mfId]
    );
  };

  const toggleSelectMatch = (matchId) => {
    if (matchId == null || matchId === '') return;
    setSelected([]);
    setSelectedMf([]);
    setSelectedMatchIds((prev) =>
      prev.includes(matchId) ? prev.filter((x) => x !== matchId) : [...prev, matchId]
    );
  };

  const cargarChequesDepositados = async () => {
    try {
      const res = await recibosCobroAPI.getCarteraCheques({ estado: 'depositado' });
      if (res.success) setChequesDepositados(res.data || []);
      else setChequesDepositados([]);
    } catch (_) {
      setChequesDepositados([]);
    }
  };

  const abrirModalCheques = async (tipo) => {
    if (tipo === 'acreditar') {
      if (matchesSeleccionados.length !== 1) {
        return toast.warning('Seleccione exactamente una línea de crédito');
      }
      if (resumenSeleccion.signo !== 'credito') {
        return toast.error('Acreditar requiere una línea de crédito');
      }
    } else {
      if (!matchesSeleccionados.length) {
        return toast.warning('Seleccione una o más líneas de débito');
      }
      if (resumenSeleccion.signo !== 'debito') {
        return toast.error('Rechazar requiere solo líneas de débito (sin créditos mezclados)');
      }
    }
    setChequeAccionIds([]);
    setFiltroChequeAccion({ texto: '', monto_min: '', monto_max: '', fecha_desde: '', fecha_hasta: '' });
    await cargarChequesDepositados();
    setModalChequesTipo(tipo);
  };

  const toggleChequeAccion = (cid) => {
    setChequeAccionIds((prev) => (prev.includes(cid) ? prev.filter((x) => x !== cid) : [...prev, cid]));
  };

  const chequesDepositadosFiltrados = useMemo(() => {
    const f = filtroChequeAccion;
    return (chequesDepositados || []).filter((ch) => {
      if (!pasaFiltroTexto(`${ch.numero || ''} ${ch.cuenta_banco_nombre || ''} ${ch.titular || ''} ${ch.caja_ubicacion_nombre || ''}`, f.texto)) {
        return false;
      }
      if (!pasaFiltroNumero(parseFloat(ch.importe) || 0, f.monto_min, f.monto_max)) return false;
      const fechaCh = ch.fecha_cobro || ch.fecha_emision || ch.fecha || '';
      if (!pasaFiltroFecha(fechaCh, f.fecha_desde, f.fecha_hasta)) return false;
      return true;
    });
  }, [chequesDepositados, filtroChequeAccion]);

  const seleccionarFiltrados = (lado) => {
    const rows = matchesFiltradosOrdenados;
    setSelectedMatchIds([]);
    if (lado === 'banco' || lado === 'ambos') {
      const ids = rows
        .filter((m) => m.estado === 'solo_banco' && m.linea_banco_id)
        .map((m) => m.linea_banco_id);
      setSelected(ids);
    }
    if (lado === 'sistema' || lado === 'ambos') {
      const ids = rows
        .filter((m) => m.estado === 'solo_sistema' && m.movimiento_fondo_id)
        .map((m) => m.movimiento_fondo_id);
      setSelectedMf(ids);
    }
  };

  const deseleccionarTodo = () => {
    setSelected([]);
    setSelectedMf([]);
    setSelectedMatchIds([]);
  };

  const confirmarChequesAccion = async () => {
    if (!modalChequesTipo || !matchesSeleccionados.length) return;
    if (!chequeAccionIds.length) return toast.error('Seleccione al menos un cheque depositado');
    if (modalChequesTipo === 'rechazar') {
      const totalDebito = resumenSeleccion.debitos;
      if (sumaChequesAccion - totalDebito > 0.01) {
        return toast.error(
          `La suma de cheques (${formatMoney(sumaChequesAccion)}) no puede superar el total de débitos (${formatMoney(totalDebito)})`
        );
      }
      const gastos = Math.round((totalDebito - sumaChequesAccion) * 100) / 100;
      if (gastos > 0.01) {
        const ok = window.confirm(
          `Débitos extracto ${formatMoney(totalDebito)} − cheques ${formatMoney(sumaChequesAccion)} = gastos ${formatMoney(gastos)}.\n¿Continuar con rechazo e imputación de gastos?`
        );
        if (!ok) return;
      } else if (Math.abs(sumaChequesAccion - totalDebito) > 0.01) {
        return toast.error('La suma de cheques debe coincidir con el total de débitos (o ser menor si hay gastos)');
      }
    }
    setImporting(true);
    try {
      const payload = {
        linea_ids: matchesSeleccionados.map((m) => m.linea_banco_id),
        cheque_ids: chequeAccionIds,
      };
      if (modalChequesTipo === 'acreditar') {
        await conciliacionesAPI.acreditarCheques(id, payload);
        toast.success('Cheques acreditados y conciliados');
      } else {
        const res = await conciliacionesAPI.rechazarCheques(id, payload);
        const g = res?.monto_gastos || 0;
        toast.success(
          g > 0
            ? `Cheques rechazados. Gastos ${formatMoney(g)} registrados. Podés reintegrarlos al cliente desde Cartera.`
            : 'Cheques rechazados, reverso generado y conciliado'
        );
      }
      setSelected([]);
      setModalChequesTipo(null);
      cargar();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setImporting(false);
    }
  };

  const vincularMatchManual = async () => {
    if (!matchesSeleccionados.length || !selectedMf.length) {
      return toast.warning('Seleccione líneas solo banco y movimientos solo sistema');
    }
    if (!puedeVincularNM) {
      return toast.error(
        `Netos no coinciden: banco ${formatMoney(resumenSeleccion.netoBanco)} vs sistema ${formatMoney(netoSistemaSel)}`
      );
    }
    setImporting(true);
    try {
      if (matchesSeleccionados.length === 1 && selectedMf.length === 1) {
        await conciliacionesAPI.matchManual(id, {
          linea_banco_id: matchesSeleccionados[0].linea_banco_id,
          movimiento_fondo_id: selectedMf[0],
        });
      } else {
        await conciliacionesAPI.matchGrupo(id, {
          lineas_banco_ids: matchesSeleccionados.map((m) => m.linea_banco_id),
          movimiento_fondo_ids: selectedMf,
        });
      }
      toast.success('Match realizado');
      setSelected([]);
      setSelectedMf([]);
      cargar();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setImporting(false);
    }
  };

  const cancelarEntreSistema = async () => {
    if (!puedeCancelarSistema) {
      return toast.error('Seleccione ≥2 movimientos solo sistema con neto ≈ 0');
    }
    if (!window.confirm(`¿Cancelar entre sí ${selectedMf.length} movimientos de sistema (neto ${formatMoney(netoSistemaSel)})?`)) {
      return;
    }
    setImporting(true);
    try {
      await conciliacionesAPI.cancelarSistema(id, { movimiento_fondo_ids: selectedMf });
      toast.success('Movimientos cancelados entre sí');
      setSelectedMf([]);
      cargar();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setImporting(false);
    }
  };

  const ignorarSeleccionado = async () => {
    if (!puedeIgnorar) {
      return toast.error('Seleccione exactamente un movimiento solo banco o solo sistema');
    }
    const lado = selected.length === 1 ? 'banco' : 'sistema';
    if (!window.confirm(`¿Marcar este movimiento de ${lado} como Ignorado?`)) return;
    setImporting(true);
    try {
      const body =
        selected.length === 1
          ? { linea_banco_id: selected[0] }
          : { movimiento_fondo_id: selectedMf[0] };
      await conciliacionesAPI.ignorar(id, body);
      toast.success('Movimiento marcado como ignorado');
      setSelected([]);
      setSelectedMf([]);
      cargar();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setImporting(false);
    }
  };

  const revertirSeleccionado = async () => {
    if (!puedeRevertir) {
      return toast.error('Seleccione filas conciliadas, ignoradas o canceladas para revertir');
    }
    if (
      !window.confirm(
        `¿Revertir ${selectedMatchIds.length} fila(s) a solo banco / solo sistema?`
      )
    ) {
      return;
    }
    setImporting(true);
    try {
      await conciliacionesAPI.revertir(id, { match_ids: selectedMatchIds });
      toast.success('Revertido');
      setSelectedMatchIds([]);
      cargar();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setImporting(false);
    }
  };

  const abrirMoverLinea = async (m) => {
    if (m.estado !== 'solo_sistema' || !m.movimiento_fondo_id) return;
    try {
      const res = await cuentasBancariasAPI.getAll({ activo: true });
      const lista = (res.success ? res.data : []) || [];
      setCuentasMover(lista.filter((c) => Number(c.id) !== Number(data.cuenta_bancaria_id)));
    } catch (_) {
      setCuentasMover([]);
    }
    setCuentaMoverId('');
    setModalMoverLinea(m);
  };

  const confirmarMoverLinea = async () => {
    if (!modalMoverLinea?.movimiento_fondo_id || !cuentaMoverId) {
      return toast.error('Seleccione la cuenta destino');
    }
    setImporting(true);
    try {
      const res = await conciliacionesAPI.moverMovimientoSistema(id, modalMoverLinea.movimiento_fondo_id, {
        cuenta_bancaria_destino_id: Number(cuentaMoverId),
      });
      toast.success(
        res.conciliacion_destino_id
          ? `Movimiento movido a conciliación #${res.conciliacion_destino_id}`
          : 'Movimiento movido de cuenta'
      );
      const mfId = modalMoverLinea.movimiento_fondo_id;
      setModalMoverLinea(null);
      setSelectedMf((prev) => prev.filter((x) => x !== mfId));
      cargar();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setImporting(false);
    }
  };

  const abrirModalImportar = async () => {
    if (!selected.length) {
      toast.warning('Seleccione filas solo banco para importar');
      return;
    }
    const sel = (data?.matches || []).filter(
      (m) => m.estado === 'solo_banco' && selected.includes(m.linea_banco_id)
    );
    let deb = 0;
    let cred = 0;
    for (const m of sel) {
      deb += parseFloat(m.banco_debito) || 0;
      cred += parseFloat(m.banco_credito) || 0;
    }
    if (cred > 0 && deb > 0) {
      toast.error('Seleccione solo créditos o solo débitos (no mezclar)');
      return;
    }
    const esCredito = cred > 0;
    setDestino(esCredito ? 'cliente' : 'gasto');
    setChequeIds([]);
    setCajaOrigenId('');
    if (esCredito) {
      try {
        const res = await recibosCobroAPI.getCarteraCheques({ estado: 'recibido' });
        if (res.success) setChequesCartera(res.data || []);
        else setChequesCartera([]);
      } catch (_) {
        setChequesCartera([]);
      }
    }
    setModalOpen(true);
  };

  const toggleCheque = (id) => {
    setChequeIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const confirmarImportar = async () => {
    if (!selected.length) return;
    if (resumenSeleccion.signo === 'mixto') {
      return toast.error('Seleccione solo créditos o solo débitos (no mezclar)');
    }
    if (destino === 'gasto' && !tipoProveedorId) return toast.error('Seleccione el tipo de gasto');
    if (destino === 'cliente' && !clienteId) return toast.error('Seleccione el cliente');
    if (destino === 'proveedor' && !proveedorId) return toast.error('Seleccione el proveedor');
    if (destino === 'empleado' && !empleadoId) return toast.error('Seleccione el empleado');
    if (destino === 'deposito_efectivo' && !cajaOrigenId) return toast.error('Seleccione la caja origen');
    if (destino === 'deposito_cheque' && !chequeIds.length) {
      return toast.error('Seleccione al menos un cheque de cartera');
    }
    if (destino === 'deposito_cheque' || destino === 'deposito_efectivo') {
      if (matchesSeleccionados.length !== 1) {
        return toast.error('Depósito: importe una sola línea de crédito');
      }
    }
    if (destino === 'deposito_cheque') {
      const montoLinea = parseFloat(matchesSeleccionados[0]?.banco_credito) || 0;
      if (Math.abs(sumaChequesSeleccionados - montoLinea) > 0.01) {
        return toast.error(
          `La suma de cheques (${formatMoney(sumaChequesSeleccionados)}) debe coincidir con el crédito (${formatMoney(montoLinea)})`
        );
      }
    }

    setImporting(true);
    try {
      await withSaldoNegativoConfirm((confirmar) =>
        conciliacionesAPI.importar(id, {
          linea_ids: selected,
          destino,
          tipo_proveedor_id: destino === 'gasto' ? Number(tipoProveedorId) : null,
          cliente_id: destino === 'cliente' ? Number(clienteId) : null,
          proveedor_id: destino === 'proveedor' ? Number(proveedorId) : null,
          empleado_id: destino === 'empleado' ? Number(empleadoId) : null,
          cheque_cartera_ids: destino === 'deposito_cheque' ? chequeIds : null,
          caja_origen_id: destino === 'deposito_efectivo' ? Number(cajaOrigenId) : null,
          ...(confirmar ? { confirmar_saldo_negativo: true } : {}),
        })
      );
      toast.success('Importación completada');
      setSelected([]);
      setModalOpen(false);
      cargar();
    } catch (e) {
      if (!e.cancelled) toast.error(e.message);
    } finally {
      setImporting(false);
    }
  };

  const reMatch = async () => {
    try {
      await conciliacionesAPI.ejecutarMatch(id);
      toast.success('Matching actualizado');
      cargar();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const cerrar = async () => {
    if (!window.confirm('¿Cerrar esta conciliación? No podrá editarla.')) return;
    try {
      await conciliacionesAPI.cerrar(id);
      toast.success('Conciliación cerrada');
      cargar();
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (loading) return <div className="loading">Cargando conciliación…</div>;
  if (!data) return <div className="error-message">Conciliación no encontrada</div>;

  const cerrada = data.estado === 'cerrada';

  return (
    <>
      <ListadoScrollLayout
        className="conciliacion-page"
        header={(
          <div className="page-header conciliacion-detalle-header">
            <div className="conciliacion-detalle-header-main">
              <Link to="/conciliacion-bancaria">← Volver</Link>
              <h1 className="conciliacion-titulo-id">Conciliación #{data.id}</h1>
              <div className="conciliacion-meta-line">
                <span className="text-muted conciliacion-meta-texto" title={`${data.banco_nombre} — ${data.numero_cuenta} · ${data.archivo_nombre}`}>
                  {data.banco_nombre} — {data.numero_cuenta} · {data.archivo_nombre}
                </span>
                {!cerrada && (
                  <div className="conciliacion-header-actions">
                    <button type="button" className="btn btn-secondary btn-sm" onClick={reMatch}>Match</button>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={abrirModalImportar}
                      disabled={!selected.length}
                    >
                      Importar ({selected.length})
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => abrirModalCheques('acreditar')}
                      disabled={matchesSeleccionados.length !== 1 || resumenSeleccion.signo !== 'credito'}
                      title="Conciliar crédito con cheques depositados → acreditado"
                    >
                      Acreditar cheques
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => abrirModalCheques('rechazar')}
                      disabled={!matchesSeleccionados.length || resumenSeleccion.signo !== 'debito'}
                      title="Rechazo bancario: 1+ débitos (cheque + gastos) → reverso + estado rechazado"
                    >
                      Rechazar cheques
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => seleccionarFiltrados('ambos')}
                      title="Seleccionar todas las filas filtradas (banco y sistema)"
                    >
                      Sel. filtradas
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={deseleccionarTodo}
                      disabled={!selected.length && !selectedMf.length && !selectedMatchIds.length}
                      title="Quitar toda la selección (banco y sistema)"
                    >
                      Desel. todo
                    </button>
                    <button
                      type="button"
                      className={
                        puedeVincularNM ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'
                      }
                      onClick={vincularMatchManual}
                      disabled={!puedeVincularNM || importing}
                      title="Vincular N líneas banco con M movimientos sistema (netos iguales)"
                    >
                      {puedeVincularNM
                        ? `Vincular ${matchesSeleccionados.length}:${selectedMf.length} ✓`
                        : `Vincular (${matchesSeleccionados.length} banco · ${selectedMf.length} sist.)`}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={cancelarEntreSistema}
                      disabled={!puedeCancelarSistema || importing}
                      title="Cancelar entre sí movimientos solo sistema con neto ≈ 0"
                    >
                      Cancelar sist.
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={ignorarSeleccionado}
                      disabled={!puedeIgnorar || importing}
                      title="Marcar un movimiento solo banco o solo sistema como Ignorado"
                    >
                      Ignorar
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={revertirSeleccionado}
                      disabled={!puedeRevertir || importing}
                      title="Revertir conciliado / ignorado / cancelado sistema a pendiente"
                    >
                      Revertir
                    </button>
                    <span className="conciliacion-neto-toolbar text-muted" title="Neto selección">
                      Neto B {formatMoney(resumenSeleccion.netoBanco)} · S {formatMoney(netoSistemaSel)}
                      {matchesSeleccionados.length && selectedMf.length
                        ? ` · Δ ${formatMoney(resumenSeleccion.netoBanco - netoSistemaSel)}`
                        : ''}
                    </span>
                    <button type="button" className="btn btn-success btn-sm" onClick={cerrar}>Cerrar</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        fixedTop={(
          <>
            <div className="conciliacion-stats">
              <div className="stat-card">
                <span className="stat-label">Neto selección</span>
                <span
                  className={`stat-value ${
                    netoSeleccionStats < 0
                      ? 'conciliacion-importe-neg'
                      : netoSeleccionStats > 0
                        ? 'conciliacion-importe-pos'
                        : ''
                  }`}
                  title={
                    selected.length && selectedMf.length
                      ? 'Δ banco − sistema'
                      : selected.length
                        ? 'Neto líneas banco'
                        : selectedMf.length
                          ? 'Neto movimientos sistema'
                          : 'Sin selección'
                  }
                >
                  {formatMoney(netoSeleccionStats)}
                </span>
              </div>
              <div className="stat-card">
                <span className="stat-label">Saldo extracto</span>
                <span className="stat-value">{formatMoney(data.saldo_extracto_final)}</span>
              </div>
              <div className="stat-card">
                <span className="stat-label">Saldo sistema</span>
                <span className="stat-value">{formatMoney(data.saldo_sistema)}</span>
              </div>
              <div className="stat-card">
                <span className="stat-label">Conciliados</span>
                <span className="stat-value">{data.resumen?.conciliados ?? 0}</span>
              </div>
              <div className="stat-card">
                <span className="stat-label">Solo banco</span>
                <span className="stat-value">{data.resumen?.solo_banco ?? 0}</span>
              </div>
              <div className="stat-card">
                <span className="stat-label">Solo sistema</span>
                <span className="stat-value">{data.resumen?.solo_sistema ?? 0}</span>
              </div>
            </div>
            {cerrada && (
              <p className="text-muted" style={{ marginBottom: 12 }}>
                Conciliación cerrada.{' '}
                <button type="button" className="btn-link" onClick={() => navigate('/conciliacion-bancaria')}>
                  Nueva conciliación
                </button>
              </p>
            )}
          </>
        )}
        filters={(
          <div className="filters conciliacion-filtros">
            <div className="filters-toolbar conciliacion-filtros-grid">
          <label className="conciliacion-filtro-estados">
            Estado
            <FiltroPredefinidosMultiselect
              label="Estado"
              emptyLabel="Todos los estados"
              oneSelectedFallback="1 estado"
              manySelectedLabel={(n) => `${n} estados seleccionados`}
              predefinidos={Object.entries(estadoLabel).map(([id, descripcion]) => ({ id, descripcion }))}
              selectedIds={filtrosMatch.estados}
              onChange={(ids) => setFiltroMatch('estados', ids)}
            />
          </label>
          <label>
            F. banco desde
            <input type="date" value={filtrosMatch.banco_fecha_desde} onChange={(e) => setFiltroMatch('banco_fecha_desde', e.target.value)} />
          </label>
          <label>
            F. banco hasta
            <input type="date" value={filtrosMatch.banco_fecha_hasta} onChange={(e) => setFiltroMatch('banco_fecha_hasta', e.target.value)} />
          </label>
          <label>
            Concepto banco
            <input
              type="text"
              value={filtrosMatch.banco_concepto}
              onChange={(e) => setFiltroMatch('banco_concepto', e.target.value)}
              placeholder="Buscar…"
            />
          </label>
          <label>
            Importe mín.
            <input type="number" step="0.01" value={filtrosMatch.importe_min} onChange={(e) => setFiltroMatch('importe_min', e.target.value)} />
          </label>
          <label>
            Importe máx.
            <input type="number" step="0.01" value={filtrosMatch.importe_max} onChange={(e) => setFiltroMatch('importe_max', e.target.value)} />
          </label>
          <label>
            F. sist. desde
            <input type="date" value={filtrosMatch.sistema_fecha_desde} onChange={(e) => setFiltroMatch('sistema_fecha_desde', e.target.value)} />
          </label>
          <label>
            F. sist. hasta
            <input type="date" value={filtrosMatch.sistema_fecha_hasta} onChange={(e) => setFiltroMatch('sistema_fecha_hasta', e.target.value)} />
          </label>
          <label>
            Concepto sistema
            <input
              type="text"
              value={filtrosMatch.sistema_concepto}
              onChange={(e) => setFiltroMatch('sistema_concepto', e.target.value)}
              placeholder="Buscar…"
            />
          </label>
          <label>
            Monto sist. mín.
            <input type="number" step="0.01" value={filtrosMatch.monto_sist_min} onChange={(e) => setFiltroMatch('monto_sist_min', e.target.value)} />
          </label>
          <label>
            Monto sist. máx.
            <input type="number" step="0.01" value={filtrosMatch.monto_sist_max} onChange={(e) => setFiltroMatch('monto_sist_max', e.target.value)} />
          </label>
          {hayFiltrosMatch && (
            <button type="button" className="btn btn-secondary btn-sm" onClick={limpiarFiltrosMatch}>
              Limpiar filtros
            </button>
          )}
        </div>
        <p className="text-muted conciliacion-filtros-count">
          Mostrando {matchesFiltradosOrdenados.length} de {(data.matches || []).length} filas
        </p>
          </div>
        )}
      >
        <table className="data-table">
          <thead>
            <tr>
              {!cerrada && <th style={{ width: 36 }} />}
              {COLUMNAS_MATCH.map((col) => (
                <th
                  key={col.id}
                  className="th-sortable"
                  onClick={() => handleSortColumn(col.id)}
                  title={`Ordenar por ${col.label}`}
                >
                  <span>{col.label}</span>
                  {sortByColumn === col.id && (
                    <span className="sort-indicator" aria-hidden="true">{sortAsc ? ' ▲' : ' ▼'}</span>
                  )}
                </th>
              ))}
              {!cerrada && <th style={{ width: 56 }} />}
            </tr>
          </thead>
          <tbody>
            {matchesFiltradosOrdenados.length === 0 && (
              <tr>
                <td colSpan={cerrada ? 7 : 9} className="text-muted">
                  {hayFiltrosMatch ? 'Ninguna fila coincide con los filtros' : 'Sin filas'}
                </td>
              </tr>
            )}
            {matchesFiltradosOrdenados.map((m) => {
              const imp = importeBanco(m);
              const impSys = importeSistema(m);
              return (
                <tr key={m.id} className={`conciliacion-row conciliacion-${m.estado}`}>
                  {!cerrada && (
                    <td>
                      {m.estado === 'solo_banco' && m.linea_banco_id && (
                        <input
                          type="checkbox"
                          checked={selected.includes(m.linea_banco_id)}
                          onChange={() => toggleSelect(m.linea_banco_id)}
                        />
                      )}
                      {m.estado === 'solo_sistema' && m.movimiento_fondo_id && (
                        <input
                          type="checkbox"
                          checked={selectedMf.includes(m.movimiento_fondo_id)}
                          onChange={() => toggleSelectMf(m.movimiento_fondo_id)}
                          title={`MF #${m.movimiento_fondo_id}`}
                        />
                      )}
                      {ESTADOS_REVERTIBLES.includes(m.estado) && (
                        <input
                          type="checkbox"
                          checked={selectedMatchIds.includes(m.id)}
                          onChange={() => toggleSelectMatch(m.id)}
                          title={`Revertir ${estadoLabel[m.estado] || m.estado}`}
                        />
                      )}
                    </td>
                  )}
                  <td>
                    <span className={`conciliacion-badge ${estadoClass[m.estado] || ''}`}>
                      {estadoLabel[m.estado] || m.estado}
                    </span>
                  </td>
                  <td>{m.banco_fecha ? formatFecha(m.banco_fecha) : '—'}</td>
                  <td className="conciliacion-concepto" title={m.banco_concepto || ''}>
                    {m.banco_concepto || '—'}
                  </td>
                  <td className={`num ${imp.signo < 0 ? 'conciliacion-importe-neg' : imp.signo > 0 ? 'conciliacion-importe-pos' : ''}`}>
                    {imp.valor !== 0 ? formatMoney(imp.valor) : ''}
                  </td>
                  <td>{m.sistema_fecha ? formatFecha(m.sistema_fecha) : '—'}</td>
                  <td className="conciliacion-concepto" title={m.sistema_concepto || ''}>
                    {m.sistema_concepto || '—'}
                  </td>
                  <td className={`num ${impSys.signo < 0 ? 'conciliacion-importe-neg' : impSys.signo > 0 ? 'conciliacion-importe-pos' : ''}`}>
                    {impSys.valor !== 0 ? formatMoney(impSys.valor) : ''}
                  </td>
                  {!cerrada && m.estado === 'solo_sistema' && m.movimiento_fondo_id ? (
                    <td>
                      <button
                        type="button"
                        className="btn-link"
                        style={{ fontSize: 12 }}
                        onClick={() => abrirMoverLinea(m)}
                        title="Mover movimiento de sistema a otra cuenta"
                      >
                        Mover
                      </button>
                    </td>
                  ) : (
                    !cerrada ? <td /> : null
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </ListadoScrollLayout>

      {modalOpen && (
        <div className="conciliacion-modal-backdrop" onClick={() => !importing && setModalOpen(false)}>
          <div className="conciliacion-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Importar al sistema</h3>
            <p className="text-muted" style={{ fontSize: 13, marginTop: 0 }}>
              {resumenSeleccion.n} línea(s) · Débitos {formatMoney(resumenSeleccion.debitos)} · Créditos {formatMoney(resumenSeleccion.creditos)}
              {resumenSeleccion.signo === 'credito' ? ' · Lote crédito' : ''}
              {resumenSeleccion.signo === 'debito' ? ' · Lote débito' : ''}
            </p>

            <div className="form-group">
              <label>Destino *</label>
              <select value={destino} onChange={(e) => setDestino(e.target.value)}>
                {destinosDisponibles.map((d) => (
                  <option key={d.id} value={d.id}>{d.label}</option>
                ))}
              </select>
            </div>

            {destino === 'gasto' && (
              <div className="form-group">
                <label>Tipo de gasto *</label>
                <select value={tipoProveedorId} onChange={(e) => setTipoProveedorId(e.target.value)}>
                  <option value="">Seleccione…</option>
                  {tiposGasto.map((t) => (
                    <option key={t.id} value={t.id}>{t.nombre}</option>
                  ))}
                </select>
              </div>
            )}
            {destino === 'cliente' && (
              <div className="form-group">
                <label>Cliente *</label>
                <select value={clienteId} onChange={(e) => setClienteId(e.target.value)}>
                  <option value="">Seleccione…</option>
                  {clientes.map((c) => (
                    <option key={c.id} value={c.id}>{c.razon_social}</option>
                  ))}
                </select>
              </div>
            )}
            {destino === 'proveedor' && (
              <div className="form-group">
                <label>Proveedor *</label>
                <select value={proveedorId} onChange={(e) => setProveedorId(e.target.value)}>
                  <option value="">Seleccione…</option>
                  {proveedores.map((p) => (
                    <option key={p.id} value={p.id}>{p.razon_social}</option>
                  ))}
                </select>
              </div>
            )}
            {destino === 'empleado' && (
              <div className="form-group">
                <label>Empleado *</label>
                <select value={empleadoId} onChange={(e) => setEmpleadoId(e.target.value)}>
                  <option value="">Seleccione…</option>
                  {empleados.map((em) => (
                    <option key={em.id} value={em.id}>{em.nombre || `#${em.id}`}</option>
                  ))}
                </select>
              </div>
            )}
            {destino === 'deposito_efectivo' && (
              <div className="form-group">
                <label>Caja origen *</label>
                <select value={cajaOrigenId} onChange={(e) => setCajaOrigenId(e.target.value)}>
                  <option value="">Seleccione…</option>
                  {cajas.map((cj) => (
                    <option key={cj.id} value={cj.id}>{cj.nombre || `#${cj.id}`}</option>
                  ))}
                </select>
              </div>
            )}
            {destino === 'deposito_cheque' && (
              <div className="form-group">
                <label>Cheques en cartera (recibido) *</label>
                <div className="conciliacion-cheque-list">
                  {!chequesCartera.length && (
                    <div className="text-muted" style={{ fontSize: 13 }}>No hay cheques en estado recibido.</div>
                  )}
                  {chequesCartera.map((ch) => (
                    <label key={ch.id} className="conciliacion-cheque-item">
                      <input
                        type="checkbox"
                        checked={chequeIds.includes(ch.id)}
                        onChange={() => toggleCheque(ch.id)}
                      />
                      <span>
                        N° {ch.numero || ch.id}
                        {ch.banco ? ` · ${ch.banco}` : ''}
                        {' · '}
                        {formatMoney(ch.importe)}
                      </span>
                    </label>
                  ))}
                </div>
                <div className="text-muted" style={{ fontSize: 12, marginTop: 6 }}>
                  Seleccionados: {formatMoney(sumaChequesSeleccionados)}
                  {matchesSeleccionados[0]
                    ? ` / crédito extracto: ${formatMoney(matchesSeleccionados[0].banco_credito)}`
                    : ''}
                </div>
              </div>
            )}

            <div className="conciliacion-modal-hint">
              {destino === 'deposito_efectivo' && (
                <>Efectivo sale de la caja y entra en la cuenta de esta conciliación
                  {data.numero_cuenta ? ` (${data.banco_nombre} — ${data.numero_cuenta})` : ''}.</>
              )}
              {destino === 'deposito_cheque' && (
                <>Los cheques pasan a depositado y el ingreso se asienta en la cuenta
                  {data.numero_cuenta ? ` (${data.banco_nombre} — ${data.numero_cuenta})` : ''}.</>
              )}
              {destino === 'credito_bancario' && (
                <>Se registra un ingreso en la cuenta (interés, préstamo u otro crédito del banco)
                  {data.numero_cuenta ? ` (${data.banco_nombre} — ${data.numero_cuenta})` : ''}. Sin caja ni cliente.</>
              )}
              {(destino === 'cliente' || destino === 'gasto' || destino === 'proveedor' || destino === 'empleado') && (
                <>Forma de pago: Transferencia bancaria en la cuenta de esta conciliación
                  {data.numero_cuenta ? ` (${data.banco_nombre} — ${data.numero_cuenta})` : ''}.</>
              )}
            </div>

            <div className="conciliacion-modal-actions">
              <button type="button" className="btn btn-secondary btn-sm" disabled={importing} onClick={() => setModalOpen(false)}>
                Cancelar
              </button>
              <button type="button" className="btn btn-primary btn-sm" disabled={importing} onClick={confirmarImportar}>
                {importing ? 'Importando…' : 'Confirmar importación'}
              </button>
            </div>
          </div>
        </div>
      )}

      {modalChequesTipo && (
        <div className="conciliacion-modal-backdrop" onClick={() => !importing && setModalChequesTipo(null)}>
          <div className="conciliacion-modal conciliacion-modal-wide" onClick={(e) => e.stopPropagation()}>
            <h3>{modalChequesTipo === 'acreditar' ? 'Acreditar cheques' : 'Rechazar cheques'}</h3>
            <p className="text-muted" style={{ fontSize: 13, marginTop: 0 }}>
              {modalChequesTipo === 'acreditar'
                ? `Crédito extracto: ${formatMoney(matchesSeleccionados[0]?.banco_credito)} · Los montos pueden diferir.`
                : `Débitos extracto (${matchesSeleccionados.length}): ${formatMoney(resumenSeleccion.debitos)} · Cheques ≤ débitos; el excedente se registra como gastos.`}
            </p>
            <div className="conciliacion-cheque-filtros">
              <input
                type="text"
                placeholder="Buscar n°, banco, librador…"
                value={filtroChequeAccion.texto}
                onChange={(e) => setFiltroChequeAccion((p) => ({ ...p, texto: e.target.value }))}
              />
              <input
                type="number"
                step="0.01"
                placeholder="Monto mín."
                value={filtroChequeAccion.monto_min}
                onChange={(e) => setFiltroChequeAccion((p) => ({ ...p, monto_min: e.target.value }))}
              />
              <input
                type="number"
                step="0.01"
                placeholder="Monto máx."
                value={filtroChequeAccion.monto_max}
                onChange={(e) => setFiltroChequeAccion((p) => ({ ...p, monto_max: e.target.value }))}
              />
              <input
                type="date"
                title="Fecha desde"
                value={filtroChequeAccion.fecha_desde}
                onChange={(e) => setFiltroChequeAccion((p) => ({ ...p, fecha_desde: e.target.value }))}
              />
              <input
                type="date"
                title="Fecha hasta"
                value={filtroChequeAccion.fecha_hasta}
                onChange={(e) => setFiltroChequeAccion((p) => ({ ...p, fecha_hasta: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label>Cheques depositados * ({chequesDepositadosFiltrados.length})</label>
              <div className="conciliacion-cheque-table-wrap">
                {!chequesDepositadosFiltrados.length && (
                  <div className="text-muted" style={{ fontSize: 13 }}>Sin cheques con estos filtros.</div>
                )}
                {!!chequesDepositadosFiltrados.length && (
                  <table className="data-table conciliacion-cheque-table">
                    <thead>
                      <tr>
                        <th style={{ width: 32 }} />
                        <th>Fecha</th>
                        <th>N°</th>
                        <th>Banco / librador</th>
                        <th className="num">Importe</th>
                        <th>Caja</th>
                      </tr>
                    </thead>
                    <tbody>
                      {chequesDepositadosFiltrados.map((ch) => {
                        const fechaCh = ch.fecha_cobro || ch.fecha_emision || ch.fecha || '';
                        return (
                          <tr key={ch.id} className={chequeAccionIds.includes(ch.id) ? 'is-selected' : ''}>
                            <td>
                              <input
                                type="checkbox"
                                checked={chequeAccionIds.includes(ch.id)}
                                onChange={() => toggleChequeAccion(ch.id)}
                              />
                            </td>
                            <td>{fechaCh ? formatFecha(fechaCh) : '—'}</td>
                            <td>{ch.numero || ch.id}</td>
                            <td className="conciliacion-concepto" title={`${ch.cuenta_banco_nombre || ''} ${ch.titular || ''}`}>
                              {[ch.cuenta_banco_nombre, ch.titular].filter(Boolean).join(' · ') || '—'}
                            </td>
                            <td className="num">{formatMoney(ch.importe)}</td>
                            <td>{ch.caja_ubicacion_nombre || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
              <div className="text-muted" style={{ fontSize: 12, marginTop: 6 }}>
                Seleccionados: {formatMoney(sumaChequesAccion)}
                {modalChequesTipo === 'acreditar'
                  && Math.abs(sumaChequesAccion - (parseFloat(matchesSeleccionados[0]?.banco_credito) || 0)) > 0.01
                  && ' (diferencia con extracto — permitido)'}
                {modalChequesTipo === 'rechazar' && resumenSeleccion.debitos - sumaChequesAccion > 0.01
                  && ` · Gastos: ${formatMoney(resumenSeleccion.debitos - sumaChequesAccion)}`}
              </div>
            </div>
            <div className="conciliacion-modal-hint">
              {modalChequesTipo === 'acreditar'
                ? 'Los cheques pasan a acreditado y se vinculan al match (sin exigir mismo importe).'
                : 'Se genera egreso del cheque (+ egreso de gastos si hay excedente), los cheques pasan a rechazado y se concilian. Luego podés reintegrar al cliente desde Cartera.'}
            </div>
            <div className="conciliacion-modal-actions">
              <button type="button" className="btn btn-secondary btn-sm" disabled={importing} onClick={() => setModalChequesTipo(null)}>
                Cancelar
              </button>
              <button type="button" className="btn btn-primary btn-sm" disabled={importing} onClick={confirmarChequesAccion}>
                {importing ? 'Procesando…' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {modalMoverLinea && (
        <div className="conciliacion-modal-backdrop" onClick={() => !importing && setModalMoverLinea(null)}>
          <div className="conciliacion-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Mover movimiento a otra cuenta</h3>
            <p className="text-muted" style={{ fontSize: 13 }}>
              MF #{modalMoverLinea.movimiento_fondo_id}
              {modalMoverLinea.sistema_concepto ? ` · ${modalMoverLinea.sistema_concepto}` : ''}
              {' · '}
              {formatMoney(importeSistema(modalMoverLinea).valor || modalMoverLinea.sistema_monto)}
            </p>
            <div className="form-group">
              <label>Cuenta destino *</label>
              <select value={cuentaMoverId} onChange={(e) => setCuentaMoverId(e.target.value)}>
                <option value="">Seleccione…</option>
                {cuentasMover.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.banco_nombre || c.banco} — {c.numero_cuenta || c.id}
                  </option>
                ))}
              </select>
            </div>
            <p className="text-muted" style={{ fontSize: 12 }}>
              Corrige la cuenta del movimiento de fondos y lo deja como solo sistema en la conciliación borrador de esa cuenta.
            </p>
            <div className="conciliacion-modal-actions">
              <button type="button" className="btn btn-secondary btn-sm" disabled={importing} onClick={() => setModalMoverLinea(null)}>
                Cancelar
              </button>
              <button type="button" className="btn btn-primary btn-sm" disabled={importing || !cuentaMoverId} onClick={confirmarMoverLinea}>
                {importing ? 'Moviendo…' : 'Mover'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const ConciliacionBancaria = () => {
  const { id } = useParams();
  const { formatFecha } = useZonaHoraria();
  const [cuentas, setCuentas] = useState([]);
  const [parsers, setParsers] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [cuentaId, setCuentaId] = useState('');
  const [parserId, setParserId] = useState('extracto_generico_csv_v1');
  const [archivo, setArchivo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();

  const parserSeleccionado = parsers.find((p) => p.id === parserId) || parsers[0];
  const acceptArchivo = parserSeleccionado?.extensiones?.length
    ? parserSeleccionado.extensiones.join(',')
    : '.pdf,.csv,.xlsx,.xls';

  useEffect(() => {
    (async () => {
      try {
        const [cRes, pRes, hRes] = await Promise.all([
          cuentasBancariasAPI.getAll({ activo: true }),
          conciliacionesAPI.listParsers(),
          conciliacionesAPI.listar(),
        ]);
        if (cRes.success) setCuentas(cRes.data || []);
        if (pRes.success && pRes.data?.length) {
          setParsers(pRes.data);
          setParserId((prev) => (pRes.data.some((p) => p.id === prev) ? prev : pRes.data[0].id));
        }
        if (hRes.success) setHistorial(hRes.data || []);
      } catch (e) {
        toast.error(e.message);
      }
    })();
  }, [id]);

  const subir = async (e) => {
    e.preventDefault();
    if (!cuentaId || !archivo) {
      toast.warning('Seleccione cuenta y archivo de extracto');
      return;
    }
    const fd = new FormData();
    fd.append('cuenta_bancaria_id', cuentaId);
    fd.append('parser_id', parserId);
    fd.append('archivo', archivo);
    try {
      setUploading(true);
      const res = await conciliacionesAPI.crear(fd);
      if (res.success && res.data?.id) {
        toast.success('Extracto procesado');
        navigate(`/conciliacion-bancaria/${res.data.id}`);
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  if (id) return <ConciliacionDetalle id={id} />;

  return (
    <div className="movimientos conciliacion-page">
      <div className="page-header">
        <h1>Conciliación bancaria</h1>
      </div>

      <form className="filters card-panel" onSubmit={subir} style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: '1.1rem', marginBottom: 12 }}>Nueva conciliación</h2>
        <div className="filters-toolbar">
          <label>
            Cuenta bancaria
            <select value={cuentaId} onChange={(e) => setCuentaId(e.target.value)} required>
              <option value="">Seleccione…</option>
              {cuentas.map((c) => (
                <option key={c.id} value={c.id}>{c.banco_nombre} — {c.numero_cuenta}</option>
              ))}
            </select>
          </label>
          <label>
            Formato extracto
            <select
              value={parserId}
              onChange={(e) => {
                setParserId(e.target.value);
                setArchivo(null);
              }}
            >
              {parsers.map((p) => (
                <option key={p.id} value={p.id}>{p.nombre}</option>
              ))}
            </select>
          </label>
          <label>
            Archivo extracto
            <input
              type="file"
              accept={acceptArchivo}
              onChange={(e) => setArchivo(e.target.files?.[0] || null)}
              required
            />
          </label>
          <span className="conciliacion-btn-subir-wrap">
            <button type="submit" className="btn btn-primary conciliacion-btn-subir" disabled={uploading}>
              {uploading ? 'Procesando…' : 'Subir y conciliar'}
            </button>
          </span>
        </div>
      </form>

      <h2 style={{ fontSize: '1.1rem' }}>Historial</h2>
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Id</th>
              <th>Cuenta</th>
              <th>Archivo</th>
              <th>Período</th>
              <th>Estado</th>
              <th>Fecha</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {historial.length === 0 && (
              <tr><td colSpan={7} className="text-muted">Sin conciliaciones previas</td></tr>
            )}
            {historial.map((h) => (
              <tr key={h.id}>
                <td>{h.id}</td>
                <td>{h.banco_nombre} {h.numero_cuenta}</td>
                <td className="conciliacion-concepto" title={h.archivo_nombre}>{h.archivo_nombre}</td>
                <td>
                  {h.periodo_desde && h.periodo_hasta
                    ? `${formatFecha(h.periodo_desde)} – ${formatFecha(h.periodo_hasta)}`
                    : '—'}
                </td>
                <td>{h.estado}</td>
                <td>{h.created_at ? formatFecha(String(h.created_at).slice(0, 10)) : '—'}</td>
                <td>
                  <Link to={`/conciliacion-bancaria/${h.id}`} className="btn btn-sm btn-secondary">Ver</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ConciliacionBancaria;
